import * as React from 'react';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import { DayOfWeek } from '@fluentui/react-calendar-compat';
import { Field, makeStyles, Switch, Text, Textarea, tokens } from '@fluentui/react-components';
import { IPollFormValues } from './pollForm';

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    },
    switchRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '16px',
        paddingTop: '12px',
        paddingBottom: '12px',
        paddingLeft: '16px',
        paddingRight: '8px',
        backgroundColor: tokens.colorNeutralBackground2,
        borderRadius: tokens.borderRadiusLarge,
    },
    switchText: {
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        minWidth: 0,
    },
    switchHint: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
    },
    dates: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: '16px',
        '@media (max-width: 767px)': {
            gridTemplateColumns: 'minmax(0, 1fr)',
        },
    },
    fullWidth: {
        width: '100%',
    },
});

const formatDate = (date?: Date | null): string => (date ? date.toLocaleDateString('ru-RU') : '');

export interface IPollSettingsFieldsProps {
    values: IPollFormValues;
    onChange: (patch: Partial<IPollFormValues>) => void;
    datesError?: string;
}

/** Response rules, completion message and the active period. */
const PollSettingsFields: React.FC<IPollSettingsFieldsProps> = ({ values, onChange, datesError }) => {
    const styles = useStyles();

    return (
        <div className={styles.root}>
            <div className={styles.switchRow}>
                <div className={styles.switchText}>
                    <Text>Один ответ на участника</Text>
                    <Text className={styles.switchHint}>Повторное прохождение с того же устройства будет заблокировано.</Text>
                </div>
                <Switch
                    checked={values.singleResponse}
                    aria-label="Один ответ на участника"
                    onChange={(_, data) => onChange({ singleResponse: !!data.checked })}
                />
            </div>

            <div className={styles.switchRow}>
                <div className={styles.switchText}>
                    <Text>Показывать результаты после завершения</Text>
                    <Text className={styles.switchHint}>Участник увидит сводку ответов, отправив свой.</Text>
                </div>
                <Switch
                    checked={values.completedDisplayResults}
                    aria-label="Показывать результаты после завершения"
                    onChange={(_, data) => onChange({ completedDisplayResults: !!data.checked })}
                />
            </div>

            <Field label="Сообщение после завершения">
                <Textarea
                    rows={2}
                    resize="vertical"
                    value={values.completedMessage}
                    onChange={(_, data) => onChange({ completedMessage: data.value })}
                />
            </Field>

            <div className={styles.dates}>
                <Field label="Дата начала" hint="Пусто — опрос доступен сразу.">
                    <DatePicker
                        className={styles.fullWidth}
                        firstDayOfWeek={DayOfWeek.Monday}
                        formatDate={formatDate}
                        value={values.opensAt ?? null}
                        allowTextInput
                        placeholder="Выберите дату"
                        onSelectDate={(date) => onChange({ opensAt: date ?? undefined })}
                    />
                </Field>
                <Field
                    label="Дата окончания"
                    hint="Пусто — без ограничения по времени."
                    validationMessage={datesError}
                >
                    <DatePicker
                        className={styles.fullWidth}
                        firstDayOfWeek={DayOfWeek.Monday}
                        formatDate={formatDate}
                        value={values.closesAt ?? null}
                        minDate={values.opensAt}
                        allowTextInput
                        placeholder="Выберите дату"
                        onSelectDate={(date) => onChange({ closesAt: date ?? undefined })}
                    />
                </Field>
            </div>
        </div>
    );
};

export default PollSettingsFields;
